/**
 * The texture library's sets (Prop Studio's photographic textures:
 * scripts/blender/textures/<id>/) as the live 3D view's pictures, so the
 * Photoreal window's preview shows the floor, the doors' wood and the framing
 * as they will be rendered — tiled at the set's real size — not only in a render.
 *
 * A setting carries a set as `set:<id>@<scale>@<rotation>` (setTexture): the
 * pipeline (renderPipeline.ts) loads the set's colour map by its id and lays
 * it at its real width times `scale`, turned `rotation` degrees, as the render
 * does. What a set's colour map is and how big it is the app says once,
 * from the library (setTextureSetResolver): the 3D code knows nothing of the
 * back ends.
 */

export interface TextureSetPicture {
  url: string;
  /** Feet one tile covers, across and along. */
  widthFt: number;
  heightFt: number;
}

export interface SetTexture { id: string; scale: number; rotation: number }

type Resolver = (id: string) => Promise<TextureSetPicture | null>;

let resolver: Resolver | null = null;
const known = new Map<string, TextureSetPicture | null>();
const pending = new Map<string, Promise<TextureSetPicture | null>>();

/** Says how a set id becomes its picture (the app's texture library). */
export function setTextureSetResolver(fn: Resolver): void {
  resolver = fn;
  pending.clear();
  for (const [id, v] of known) if (v === null) known.delete(id);
}

/** A set's picture: known at once, or fetched (resolve with it, null when the set or its colour map is not there). */
export function textureSetPicture(id: string): Promise<TextureSetPicture | null> {
  if (known.has(id)) return Promise.resolve(known.get(id) ?? null);
  if (!resolver) return Promise.resolve(null);
  let p = pending.get(id);
  if (!p) {
    p = resolver(id).catch(() => null).then((pic) => { known.set(id, pic); pending.delete(id); return pic; });
    pending.set(id, p);
  }
  return p;
}

/** A set's size once its picture is known (feet), or null. */
export function textureSetSize(id: string): { widthFt: number; heightFt: number } | null {
  const pic = known.get(id);
  return pic ? { widthFt: pic.widthFt, heightFt: pic.heightFt } : null;
}

const SET = /^set:([a-z0-9_]{1,80})@([0-9.]+)@(-?[0-9.]+)$/;

/** The setting for a placement of a set. */
export function setTexture(id: string, scale: number, rotation: number): string {
  return `set:${id}@${+scale.toFixed(3)}@${+rotation.toFixed(1)}`;
}

/** A setting's set, or null when it names a built-in picture or an upload. */
export function parseSetTexture(value: string | null | undefined): SetTexture | null {
  const m = value ? SET.exec(value) : null;
  return m ? { id: m[1], scale: Math.max(0.01, parseFloat(m[2]) || 1), rotation: parseFloat(m[3]) || 0 } : null;
}
